import { css } from "@emotion/css";
import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { ActionButtonType, Badge, EmptyStateNoResults, InlineEmptyStateOnboarding, List, ListTitle, ListItemContent, ListItemLink, Button, ThirdPartyLogo, Pagination, SearchInput, formatExternalUrl } from "@octopusdeploy/design-system-components";
import { PlayIcon } from "@octopusdeploy/design-system-icons";
import { DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionTemplateResource, Repository, ResourceCollection } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { useCallback } from "react";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import MarkdownDescription from "~/components/MarkdownDescription";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import { resolveUrl } from "~/utils/resolveUrl";
import ImportActionTemplateDialog from "./ImportActionTemplateDialog";
export interface StepTemplatesQueryParams {
    templateName: string;
    templateType: TemplateType;
    page: number | undefined;
    pageSize: number | undefined;
}
export async function stepTemplatesPageLoader(repository: Repository, queryParams: StepTemplatesQueryParams): Promise<StepTemplatesPageLoaderData> {
    const actionTemplates = getPaginatedActionTemplates(repository, queryParams.page, queryParams.pageSize, queryParams.templateName, queryParams.templateType);
    const unfilteredActionTemplateCount = getUnfilteredActionTemplateCount(repository);
    return {
        actionTemplates: await actionTemplates,
        unfilteredActionTemplateCount: await unfilteredActionTemplateCount,
    };
}
export interface StepTemplatesPageLoaderData {
    actionTemplates: ResourceCollection<ActionTemplateResource>;
    unfilteredActionTemplateCount: number;
}
export const stepTemplatesPageTitle = "Step Templates";
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
interface StepTemplatesPageProps {
    spaceId: string;
    loaderData: StepTemplatesPageLoaderData;
    queryParams: StepTemplatesQueryParams;
    setQueryParams: QueryParamValuesSetter<StepTemplatesQueryParams>;
}
type TemplateType = "all" | "custom" | "community";
export function StepTemplatesPage({ spaceId, loaderData, queryParams, setQueryParams }: StepTemplatesPageProps) {
    const { result: actionTemplates } = usePaginatedActionTemplatesQuery(loaderData, queryParams);
    const [debouncedActionTemplateName, setDebouncedActionTemplateName] = useDebouncedExternalState(queryParams.templateName, (value) => setQueryParams({ templateName: value, page: 1 }), 200);
    const navigate = useSpaceAwareNavigation();
    const createStepTemplatePageAction: PrimaryPageAction = {
        type: "navigate",
        label: "Create Step Template",
        path: links.selectBuiltInStepTemplatePage.generateUrl({ spaceId }),
    };
    const browseCommunityStepsPageAction = {
        type: "navigate",
        label: "Browse Community Steps",
        path: links.communityStepTemplatesPage.generateUrl({ spaceId }),
        buttonType: "secondary",
    } satisfies PageAction;
    const pageActions: PageAction[] = [
        {
            type: "custom",
            key: "import",
            content: (<OpenDialogButton key="import" label="Import" type={ActionButtonType.Secondary}>
                    <ImportActionTemplateDialog onSaveDone={(template) => navigate.navigate(links.stepTemplateDetailsPage.generateUrl({ spaceId, templateId: template.Id }))}/>
                </OpenDialogButton>),
        },
        browseCommunityStepsPageAction,
    ];
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    if (loaderData.unfilteredActionTemplateCount === 0) {
        return (<PageContent header={{ title: stepTemplatesPageTitle, primaryAction: createStepTemplatePageAction, pageActions }}>
                <BorderedContent>
                    <InlineEmptyStateOnboarding illustration={"NoEntities"} entityName="Step Templates" actions={{
                primary: createStepTemplatePageAction,
                secondary: browseCommunityStepsPageAction,
            }} learnMore={{ path: formatExternalUrl("CustomStepTemplates"), label: "Docs: Step Templates" }}/>
                </BorderedContent>
            </PageContent>);
    }
    const hideBottomPagination = actionTemplates.Items.length < 20;
    return (<PageContent header={{ title: stepTemplatesPageTitle, primaryAction: createStepTemplatePageAction, pageActions }} pagination={{
            ui: (<Pagination label={"Step Templates"} totalResults={actionTemplates.TotalResults} itemsPerPageOptions={pageSizeOptions} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} selectedItemsPerPage={actionTemplates.ItemsPerPage}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} filters={{
            inputs: [
                <SearchInput autoFocus value={debouncedActionTemplateName} onChange={setDebouncedActionTemplateName} accessibleName={"Search step template by name"} placeholder="Search by name"/>,
                <div className={pageStyles.selectWrapper}>
                        <DeprecatedSelect value={queryParams.templateType} 
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                onChange={(value) => setQueryParams((params) => ({ ...params, templateType: value as TemplateType }))} items={[
                        { value: "all", text: "All templates" },
                        { value: "community", text: "Community templates" },
                        { value: "custom", text: "Custom templates" },
                    ]} sortItems={false} pageLayoutV2={true}/>
                    </div>,
            ],
        }}>
            <BorderedContent>
                <List<ActionTemplateResource> items={actionTemplates.Items} renderRow={({ item: actionTemplate }) => buildRow(actionTemplate)} empty={<EmptyStateNoResults />}/>
            </BorderedContent>
        </PageContent>);
    function buildRow(actionTemplate: ActionTemplateResource) {
        return (<ListItemLink href={links.stepTemplateDetailsPage.generateUrl({ spaceId: actionTemplate.SpaceId, templateId: actionTemplate.Id })} key={actionTemplate.Id}>
                <ListItemContent>
                    <div className={templateItemStyles.root}>
                        <div className={templateItemStyles.titleAndDescription}>
                            <div className={templateItemStyles.title}>
                                <ThirdPartyLogo url={resolveUrl(actionTemplate.Links.Logo)}/>
                                <ListTitle>{actionTemplate.Name}</ListTitle>
                            </div>
                            <MarkdownDescription markup={actionTemplate.Description}/>
                        </div>

                        <Badge variant="neutral" size="medium" label={actionTemplate.CommunityActionTemplateId ? "Community template" : "Custom template"}/>

                        <div className={templateItemStyles.actions}>
                            {!actionTemplate.CommunityActionTemplateId && actionTemplate.ActionType === "Octopus.Script" && (<Button label="Run" importance="quiet" icon={<PlayIcon size={24}/>} href={links.runStepTemplatePage.generateUrl({ spaceId: actionTemplate.SpaceId, templateId: actionTemplate.Id })}/>)}
                        </div>
                    </div>
                </ListItemContent>
            </ListItemLink>);
    }
}
const pageStyles = {
    selectWrapper: css({
        width: "14rem",
        // The Select component has a different height to the SearchInput
        // It's very difficult to modify the spacing that is hardcoded into the Select, it's designed to be a specific size
        // To get the Select and SearchInput to line up properly, this negative margin a hack to reposition it without touching internals
        marginTop: `-${space[16]}`,
    }),
};
const templateItemStyles = {
    root: css({
        display: "flex",
        alignItems: "center",
        gap: space[16],
    }),
    titleAndDescription: css({
        flex: 1,
    }),
    title: css({
        display: "flex",
        alignItems: "center",
        gap: space[16],
    }),
    actions: css({
        width: "5rem",
    }),
};
function getPaginatedActionTemplates(repository: Repository, page: number = 1, pageSize: number = defaultPageSize, templateName: string = "", templateType: TemplateType = "all") {
    return repository.ActionTemplates.list({
        skip: (page - 1) * pageSize,
        take: pageSize,
        partialName: templateName,
        isCommunityActionTemplate: templateType === "all" ? undefined : templateType === "community",
    });
}
function usePaginatedActionTemplatesQuery(initialResult: StepTemplatesPageLoaderData, queryParams: StepTemplatesQueryParams) {
    const { page, pageSize, templateName, templateType } = queryParams;
    return useQuery((repository) => getPaginatedActionTemplates(repository, page, pageSize, templateName, templateType), [page, pageSize, templateName, templateType], "Get paginated action templates", {
        initialResult: initialResult.actionTemplates,
    });
}
async function getUnfilteredActionTemplateCount(repository: Repository) {
    const actionTemplates = await repository.ActionTemplates.list({
        skip: 0,
        take: 0,
    });
    return actionTemplates.TotalResults;
}
